import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { api, ApiError } from "@/lib/api-client";
import {
  useWizardStore,
  type DomainKind,
  type SiteTypeChoice,
  type VoiceNumberType,
} from "@/state/wizard-store";

/**
 * The add-on tier that includes AI phone support. Kept as a literal rather
 * than imported from the backend's ADDON_SLUGS because this bundle never
 * imports from functions/ — the two are asserted equal by the questionnaire
 * test.
 */
const VOICE_ADDON_SLUG = "axiss-support-voice";

const SITE_TYPES: { value: SiteTypeChoice; label: string; hint: string }[] = [
  { value: "ecommerce", label: "Ecommerce", hint: "Sell physical or digital products" },
  { value: "saas", label: "SaaS / Marketing", hint: "Marketing site with pricing + signup" },
  { value: "museum", label: "Museum", hint: "Cultural institution — visit, exhibits, donate" },
  { value: "lodging", label: "Lodging", hint: "Short-term rentals — direct bookings" },
  { value: "social_casino", label: "Social Casino", hint: "Play-for-fun games shell" },
];

export function Questionnaire() {
  const navigate = useNavigate();
  const setQuestionnaire = useWizardStore((s) => s.setQuestionnaire);
  const setJobId = useWizardStore((s) => s.setJobId);
  const persisted = useWizardStore((s) => s.questionnaire);
  const purchasedAddons = useWizardStore((s) => s.addons) ?? [];

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const [siteName, setSiteName] = useState(persisted.siteName ?? "");
  const [siteType, setSiteType] = useState<SiteTypeChoice>(persisted.siteType ?? "ecommerce");
  const [tagline, setTagline] = useState(persisted.tagline ?? "");
  const [description, setDescription] = useState(persisted.description ?? "");
  const [colorMode, setColorMode] = useState<"ai" | "hex">(persisted.primaryColor ? "hex" : "ai");
  const [primaryColor, setPrimaryColor] = useState(persisted.primaryColor ?? "#4F8E5C");
  const [domainKind, setDomainKind] = useState<DomainKind>(persisted.domain?.kind ?? "subdomain");
  const [domainValue, setDomainValue] = useState(persisted.domain?.value ?? "");
  const [adminEmail, setAdminEmail] = useState(persisted.adminEmail ?? "");
  // Lodging only: Airbnb listing URLs to import after provisioning. Always at
  // least one (empty) row so the input renders.
  const [airbnbUrls, setAirbnbUrls] = useState<string[]>(
    persisted.airbnbListingUrls?.length ? persisted.airbnbListingUrls : [""],
  );
  // Voice bundle only. Toll-free is the default because it is what a support
  // line normally wants and the reason most customers buy the tier.
  const [voiceNumberType, setVoiceNumberType] = useState<VoiceNumberType>(
    persisted.voiceNumberType ?? "toll_free",
  );
  const [voiceAreaCode, setVoiceAreaCode] = useState(persisted.voiceAreaCode ?? "");
  const boughtVoice = purchasedAddons.includes(VOICE_ADDON_SLUG);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[] | null>(null);
  // The platform refuses a build with no plan behind it (or a plan whose one
  // site already exists). The way forward is the plan page, not a retry.
  const [needsPlan, setNeedsPlan] = useState(false);

  // Default the admin email to whatever the signed-in user has.
  useEffect(() => {
    if (!adminEmail && me?.email) setAdminEmail(me.email);
  }, [me, adminEmail]);

  // Auto-suggest a subdomain from the site name unless the user has typed one.
  useEffect(() => {
    if (domainKind !== "subdomain") return;
    if (domainValue && domainValue !== suggestSubdomain(siteName, "")) return;
    setDomainValue(suggestSubdomain(siteName, domainValue));
  }, [siteName, domainKind, domainValue]);

  const domainPreview = useMemo(() => {
    if (!domainValue) return "";
    return domainKind === "subdomain"
      ? `${domainValue}.rumio.world`
      : domainValue;
  }, [domainKind, domainValue]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setIssues(null);
    setNeedsPlan(false);

    // Only carry Airbnb URLs for lodging sites, trimmed + de-blanked.
    const cleanedAirbnbUrls =
      siteType === "lodging" ? airbnbUrls.map((u) => u.trim()).filter(Boolean) : [];

    const payload = {
      siteName,
      siteType,
      tagline: tagline || undefined,
      description,
      primaryColor: colorMode === "hex" ? primaryColor : undefined,
      domain: { kind: domainKind, value: domainValue },
      adminEmail,
      airbnbListingUrls: cleanedAirbnbUrls.length ? cleanedAirbnbUrls : undefined,
      // Add-ons bought at checkout, so the wizard provisions the integrations
      // the customer actually paid for.
      addons: purchasedAddons.length ? purchasedAddons : undefined,
      // Only meaningful alongside the voice tier; the server ignores them
      // otherwise, so a stale store value can never buy a number.
      ...(boughtVoice
        ? {
            voiceNumberType,
            ...(voiceAreaCode ? { voiceAreaCode } : {}),
          }
        : {}),
    };

    // Snapshot the answers so a refresh during provisioning doesn't lose them.
    setQuestionnaire({
      siteName,
      siteType,
      tagline,
      description,
      primaryColor: colorMode === "hex" ? primaryColor : null,
      domain: { kind: domainKind, value: domainValue },
      adminEmail,
      airbnbListingUrls: cleanedAirbnbUrls,
      ...(boughtVoice ? { voiceNumberType, voiceAreaCode } : {}),
    });

    try {
      const { job_id } = await api.submitQuestionnaire(payload);
      setJobId(job_id);
      navigate(`/provisioning/${job_id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setIssues(err.issues ?? null);
        setNeedsPlan(err.code === "subscription_required");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  }

  return (
    <Shell
      title="Tell us about your site"
      subtitle="Six quick questions. Everything else, our AI handles — you can customize it after launch."
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label="Site name" htmlFor="siteName">
          <input
            id="siteName"
            required
            minLength={2}
            maxLength={80}
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="e.g. NewCo Goods"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
          />
        </Field>

        <Field label="Tagline (optional)" htmlFor="tagline" hint="A short slogan or motto. Helps our AI capture your voice.">
          <input
            id="tagline"
            maxLength={120}
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="e.g. Built to last, shipped with care"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
          />
          <span className="text-xs" style={{ color: "var(--color-muted)" }}>
            {tagline.length} / 120
          </span>
        </Field>

        <Field label="What kind of site?" htmlFor="siteType">
          <select
            id="siteType"
            value={siteType}
            onChange={(e) => setSiteType(e.target.value as SiteTypeChoice)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
          >
            {SITE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label} — {t.hint}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Describe what you do"
          htmlFor="description"
          hint="One or two sentences. Be specific — mention your location, what makes you different, and who your customers are. We use this to generate your unique content."
        >
          <textarea
            id="description"
            required
            minLength={10}
            maxLength={200}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Hand-built widgets for woodworking hobbyists"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
          />
          <span className="text-xs" style={{ color: "var(--color-muted)" }}>
            {description.length} / 200
          </span>
        </Field>

        {siteType === "lodging" && (
          <Field
            label="Your Airbnb listings (optional)"
            hint="Paste the public link to each Airbnb listing. After your site is built, you'll review and import the details, photos, and reviews on your dashboard."
          >
            <div className="space-y-2">
              {airbnbUrls.map((u, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={u}
                    onChange={(e) =>
                      setAirbnbUrls((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                    }
                    placeholder="https://www.airbnb.com/rooms/12345678"
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    style={inputStyle()}
                  />
                  {airbnbUrls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setAirbnbUrls((prev) => prev.filter((_, j) => j !== i))}
                      className="shrink-0 px-2 text-sm"
                      style={{ color: "var(--color-muted)" }}
                      aria-label="Remove this listing"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setAirbnbUrls((prev) => [...prev, ""])}
                className="text-sm underline"
                style={{ color: "var(--color-primary)" }}
              >
                + Add another listing
              </button>
            </div>
          </Field>
        )}

        {boughtVoice && (
          <Field
            label="Your support phone number"
            hint="We'll buy this number and point it at your AI phone agent while your site is being built."
          >
            <div className="flex flex-wrap items-center gap-4">
              {([
                { value: "toll_free", label: "Toll-free" },
                { value: "local", label: "Local" },
              ] as const).map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="voiceNumberType"
                    checked={voiceNumberType === opt.value}
                    onChange={() => setVoiceNumberType(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
              <input
                value={voiceAreaCode}
                onChange={(e) => setVoiceAreaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                placeholder="Area code"
                aria-label="Preferred area code"
                inputMode="numeric"
                maxLength={3}
                className="w-28 rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              />
            </div>
            <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
              Your number takes calls as soon as your site is live. Text messaging is a separate
              step: carriers require{" "}
              {voiceNumberType === "toll_free" ? "toll-free verification" : "A2P 10DLC registration"}{" "}
              before a business number can send, which takes days to weeks and needs your site to be
              publicly live first. We'll walk you through it from your dashboard. Area code is a
              preference — if none are free we'll pick the closest available.
            </p>
          </Field>
        )}

        <Field label="Primary color">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="colorMode"
                checked={colorMode === "ai"}
                onChange={() => setColorMode("ai")}
              />
              Let AI pick
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="colorMode"
                checked={colorMode === "hex"}
                onChange={() => setColorMode("hex")}
              />
              Pick a color
            </label>
            {colorMode === "hex" && (
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-9 w-12 rounded border"
                style={{ borderColor: "var(--color-border)" }}
              />
            )}
          </div>
        </Field>

        <Field label="Domain" hint="You can add a custom domain later from your tenant admin too.">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="domainKind"
                checked={domainKind === "subdomain"}
                onChange={() => setDomainKind("subdomain")}
              />
              Subdomain on rumio.world
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="domainKind"
                checked={domainKind === "byo"}
                onChange={() => setDomainKind("byo")}
              />
              Use my own domain
            </label>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              required
              value={domainValue}
              onChange={(e) => setDomainValue(e.target.value.toLowerCase())}
              placeholder={domainKind === "subdomain" ? "newco" : "newco.com"}
              className="flex-1 rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
            />
            {domainKind === "subdomain" && (
              <span className="text-sm" style={{ color: "var(--color-muted)" }}>
                .rumio.world
              </span>
            )}
          </div>
          {domainPreview && (
            <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
              Site will live at <strong>{domainPreview}</strong>
            </p>
          )}
        </Field>

        <Field label="Admin email" htmlFor="adminEmail" hint="This becomes the super-admin of your new tenant.">
          <input
            id="adminEmail"
            type="email"
            required
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
          />
        </Field>

        {error && (
          <div
            className="rounded-md border p-3 text-sm"
            style={{
              borderColor: "#dc2626",
              background: "#fef2f2",
              color: "#7f1d1d",
            }}
          >
            <p className="font-medium">{error}</p>
            {needsPlan && (
              <div className="mt-2">
                <Button type="button" onClick={() => navigate("/welcome")}>
                  Choose a plan
                </Button>
              </div>
            )}
            {issues && (
              <ul className="mt-1 list-disc pl-5 text-xs">
                {issues.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Starting…" : "Build my site"}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    background: "var(--color-card)",
    borderColor: "var(--color-border)",
    color: "var(--color-fg)",
  };
}

function suggestSubdomain(siteName: string, currentValue: string): string {
  const slug = siteName
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
  // Don't clobber a manual edit — only auto-fill when the current value is
  // empty or still matches the previously-derived slug.
  if (currentValue && currentValue !== slug.slice(0, currentValue.length)) {
    return currentValue;
  }
  return slug;
}
